import { useSDKConfigStore } from "../stores/sdkConfigStore";
import { getCreditDelegationTxData, getCreditInformation } from "../api/api";
import { useCreditStore } from "../stores/creditStore";

interface DelegateCardParams {
  creditType: "aave-v3" | "euler" | any;
  creditLimit: number;
  sendTransactionAsync: (params: {
    to: string;
    value: bigint;
    data: string;
  }) => Promise<string>;

  userParams?:
    | {
        vaults: string[];
        borrowVaultAddress: string;
      }
    | {
        borrowTokenAddress: string;
      };
}

export async function delegateAmount({
  creditLimit,
  creditType,
  sendTransactionAsync,
  userParams,
}: DelegateCardParams): Promise<{
  success: boolean;
  message: string;
  transactionHash?: string;
}> {
  const { eoaAddress } = useSDKConfigStore.getState();

  if (!eoaAddress) {
    return {
      success: false,
      message: "EOA address not found. Please initialize the SDK first.",
    };
  }

  if (creditLimit < 0) {
    return {
      success: false,
      message: "Credit limit must be equal or greater than 0.",
    };
  }

  try {
    console.log("[delegateCard] Step 1: Fetching credit delegation tx data...");

    const delegationResponse = await getCreditDelegationTxData({
      from: eoaAddress,
      creditLimit,
      creditType,
      userParams,
    });

    const delegation = delegationResponse.data;
    if (!delegation.to || !delegation.callData) {
      return {
        success: false,
        message: "Invalid transaction data received from delegation service.",
      };
    }

    console.log("[delegateCard] Step 2: Sending delegation transaction...");

    const transactionHash = await sendTransactionAsync({
      to: delegation.to,
      value: BigInt(delegation.value || 0),
      data: delegation.callData,
    });
    console.log("[delegateCard] Transaction sent:", transactionHash);

    console.log("[delegateCard] Delegation completed successfully.");

    // fetch position data and confirm that card was delegated
    const info = await getCreditInformation(eoaAddress, creditType);

    // Update the credit info in the store
    let storeType: "aave" | "euler" | string =
      creditType === "aave-v3"
        ? "aave"
        : creditType === "euler"
        ? "euler"
        : creditType;

    useCreditStore.getState()._setCreditInfo(storeType, info);

    // For Euler and others, just return success after updating store
    return {
      success: true,
      message: "Your collateral approval was successful.",
      transactionHash,
    };
  } catch (err) {
    console.error("[delegateCard] Error:", err);
    return {
      success: false,
      message: "Failed to delegate amount.",
    };
  }
}
